import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, Repeat, SkipBack } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { scenarioById } from '../scenarios/index.ts';
import { timelineOf } from '../app/scene.ts';
import { IconButton, Segmented } from '../ui/primitives.tsx';
import { textWidth } from '../ui/Chart.tsx';
import { placeLabels } from '../ui/chart.ts';
import { formatSpeed, formatTime } from './time.ts';
import { JourneyTransport } from './Journey.tsx';

export function Timeline() {
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  const time = useAtlas((s) => s.time);
  const playing = useAtlas((s) => s.playing);
  const speed = useAtlas((s) => s.speed);
  const paced = useAtlas((s) => s.paced);
  const setTime = useAtlas((s) => s.setTime);
  const setPlaying = useAtlas((s) => s.setPlaying);
  const setSpeed = useAtlas((s) => s.setSpeed);
  const setPaced = useAtlas((s) => s.setPaced);
  const track = useRef<HTMLDivElement>(null);
  const tl = useAtlas(timelineOf);
  const [trackW, setTrackW] = useState(0);
  useLayoutEffect(() => {
    const el = track.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setTrackW(el.clientWidth));
    ro.observe(el);
    setTrackW(el.clientWidth);
    return () => ro.disconnect();
  }, [tl]);
  // Event labels sit centred over their ticks, inside the track; one that would overlap another keeps only its tick.
  // The track runs from the timeline's start (0, or a run's first frame after it) to its end.
  const t0 = tl?.start ?? 0;
  const span = tl ? tl.duration - t0 : 0;
  const along = (t: number) => (span > 0 ? (t - t0) / span : 0);
  const labelLefts = useMemo(() => (tl && trackW ? placeLabels((tl.events ?? []).map((e) => ({ x: along(e.t) * trackW, w: textWidth(e.label), rank: e.rank })), 0, trackW) : []), [tl, trackW]);
  if (!tl) return null;
  const frac = Math.min(1, Math.max(0, along(time)));

  const seek = (clientX: number) => {
    const r = track.current?.getBoundingClientRect();
    if (!r) return;
    setTime(t0 + Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * span);
  };
  const step = span / 100;

  return (
    <div className="timeline" role="group" aria-label="Timeline" data-inset="bottom" style={{ ['--sc' as string]: `var(--sc-${scenario.id})` }}>
      <div className="tl-transport">
        <IconButton icon={SkipBack} label="Back to start" keys={['Home']} tipSide="top" onClick={() => setTime(t0)} />
        <IconButton className="tl-play" icon={playing ? Pause : Play} label={playing ? 'Pause' : 'Play'} keys={['Space']} tipSide="top" onClick={() => setPlaying(!playing)} />
        <JourneyTransport />
      </div>
      <div className="tl-readout num" aria-live="off">
        <span className="tl-now">{formatTime(time, tl.unit, tl.origin)}</span>
        <span className="tl-total">{formatTime(tl.duration, tl.unit, tl.origin)}</span>
      </div>
      <div
        ref={track}
        className="tl-track"
        role="slider"
        tabIndex={0}
        aria-label="Scenario time"
        aria-valuemin={t0}
        aria-valuemax={tl.duration}
        aria-valuenow={time}
        aria-valuetext={formatTime(time, tl.unit, tl.origin)}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          seek(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons & 1) seek(e.clientX);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') setTime(Math.min(tl.duration, time + (e.shiftKey ? step * 10 : step)));
          else if (e.key === 'ArrowLeft') setTime(Math.max(t0, time - (e.shiftKey ? step * 10 : step)));
          else return;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="tl-rail">
          <div className="tl-fill" style={{ width: `${frac * 100}%` }} />
        </div>
        {(tl.events ?? []).map((ev, i) => {
          const left = labelLefts[i];
          return (
            <div key={`${i}:${ev.t}:${ev.label}`} className="tl-event" style={{ left: `${along(ev.t) * 100}%` }} aria-label={ev.label || undefined}>
              {left != null ? (
                <span className="tl-event-label" style={{ left: left - along(ev.t) * trackW + 1, transform: 'none' }}>
                  {ev.label}
                </span>
              ) : null}
            </div>
          );
        })}
        <div className="tl-head" style={{ left: `${frac * 100}%` }} />
      </div>
      <div className="tl-options">
        {tl.periodic ? (
          <span className="tl-loop" title="Loops: one cardiac cycle">
            <Repeat size={13} strokeWidth={1.5} />
          </span>
        ) : null}
        <Segmented
          size="sm"
          label="Speed"
          value={paced && tl.pacing ? 'paced' : String(speed)}
          options={[...(tl.pacing ? [{ value: 'paced', label: 'Paced' }] : []), ...tl.speeds.map((v) => ({ value: String(v), label: formatSpeed(v) }))]}
          onChange={(v) => (v === 'paced' ? setPaced(true) : setSpeed(Number(v)))}
        />
      </div>
    </div>
  );
}
